// computeCase — orchestrates normalisation, taxes, guideline, proposed, cash flow and per-child allocation.
import {
  CCE_ALLOCATION_LABEL, CS_METHOD_LABEL, SS_METHOD_LABEL, defaultSettings, otherParty,
  type Case, type CaseResult, type GuidelineResult, type PartyIndex, type PartyResult, type PerChildResult, type ProposedResult, type SupportLine,
} from '@divorceos/schema';
import { partyScenario, type ScenarioInputs } from './cashflow.js';
import { computeGuideline, payorOf, receivedBy, signedBy, type GuidelineOutcome } from './guideline.js';
import { normalizeCase, sortChildrenOldestFirst, type NCase } from './normalize.js';
import { computeProposed, type ProposedOutcome } from './proposed.js';
import { providerOf, type EngineOptions } from './provider.js';
import { NO_SUPPORT, TaxContext } from './taxes.js';

/** SupportLine from a p0-signed amount. */
export const toLine = (signed: number): SupportLine => ({ amount: Math.abs(signed), payor: payorOf(signed) });

const money = (v: number) => `$${Math.round(Math.abs(v)).toLocaleString('en-US')}`;

/** "<Payor> pays <amount> to <Payee>" sentence(s) for a p0-signed CS, SS and add-on triple. */
export function paymentDescription(labels: [string, string], cs: number, ss: number, addOns: number, what: string): string {
  const parts: { label: string; signed: number }[] = [
    { label: 'child support', signed: cs }, { label: 'spousal support', signed: ss }, { label: 'add-ons', signed: addOns },
  ].filter((x) => Math.abs(x.signed) >= 0.5);
  if (!parts.length) return `No ${what} support is payable.`;
  const total = cs + ss + addOns;
  const payor = payorOf(total);
  const oneWay = parts.every((x) => Math.sign(x.signed) === Math.sign(total));
  if (payor !== null && oneWay) {
    const detail = parts.length > 1 ? ` (${parts.map((x) => `${x.label} ${money(x.signed)}`).join(' + ')})` : ` ${parts[0].label}`;
    return `${labels[payor]} pays ${money(total)} per month${detail} to ${labels[otherParty(payor)]} under the ${what} column.`;
  }
  return parts.map((x) => {
    const p = payorOf(x.signed) as PartyIndex;
    return `${labels[p]} pays ${money(x.signed)} ${x.label} to ${labels[otherParty(p)]}`;
  }).join('; ') + ` (${what}).`;
}

/**
 * §2.7 per-child allocation (FUN_10002668): children are in DOB order (oldest first); the case is recomputed with
 * children k..N−1 kept and each party's allocable add-on items (child care, visits/school/travel, uninsured health)
 * scaled to (N−k)/N; child k = result(k) − result(k+1) for CS, add-ons and total.
 */
function perChildAllocation(nc: NCase, opts: EngineOptions, g: GuidelineOutcome): PerChildResult[] {
  const N = nc.children.length;
  if (N === 0) return [];
  const provider = providerOf(opts);
  const now = opts.now ?? new Date();
  const results: { cs: number; addOns: number; total: number }[] = [];
  results.push({ cs: g.presumedCs, addOns: g.addOns, total: g.presumedCs + g.addOns });
  const sorted = sortChildrenOldestFirst(nc.c.children.slice(0, N));
  for (let k = 1; k < N; k++) {
    const frac = (N - k) / N;
    const parties = nc.c.parties.map((p) => ({
      ...p,
      childCare: { ...p.childCare, thisRelationship: p.childCare.thisRelationship * frac, visitsSchoolTravel: p.childCare.visitsSchoolTravel * frac, uninsuredHealth: p.childCare.uninsuredHealth * frac },
    })) as Case['parties'];
    const sub = normalizeCase({ ...nc.c, parties, children: sorted.slice(k) });
    const ctx = new TaxContext(sub, provider);
    const gk = computeGuideline(ctx, { releases: 0, releaseFrom: 1, now });
    results.push({ cs: gk.presumedCs, addOns: gk.addOns, total: gk.presumedCs + gk.addOns });
  }
  results.push({ cs: 0, addOns: 0, total: 0 });
  const userCs = nc.c.settings.csMethod === 'USER';
  const items = nc.parties.map((p) => p.addOnItems / N);
  return nc.children.map((ch, k) => {
    let cs = results[k].cs - results[k + 1].cs;
    if (userCs && ch.userCs !== undefined) cs = signedBy(ch.userCs, ch.userCsPayor ?? nc.c.userSupport.csPayor);
    const addOns = results[k].addOns - results[k + 1].addOns;
    const withP0 = ch.custodialParent === 0 ? 1 - ch.ncpFraction : ch.ncpFraction;
    return {
      name: ch.name, dateOfBirth: ch.dateOfBirth,
      timeshare: [withP0 * 100, (1 - withP0) * 100] as [number, number],
      childCare: [items[0], items[1]] as [number, number],
      addOns: toLine(addOns), basicCs: toLine(cs), presumedCs: toLine(cs + addOns),
    };
  });
}

function guidelineResult(nc: NCase, g: GuidelineOutcome): GuidelineResult {
  const s = nc.c.settings;
  return {
    kFactor: s.csOnAdjustedNets ? g.kAdjusted : g.k,
    hPercent: g.hPercent,
    higherEarner: g.hi,
    basicCs: toLine(g.presumedCs),
    addOns: toLine(g.addOns),
    ss: toLine(g.ss),
    bump: g.bump,
    total: toLine(g.total),
    unadjustedCs: g.rawCs,
    unadjustedSs: g.rawSs,
    lowIncome: g.lowIncome,
    csMethodLabel: CS_METHOD_LABEL[s.csMethod],
    ssMethodLabel: SS_METHOD_LABEL[s.ssMethod],
    cceAllocationLabel: CCE_ALLOCATION_LABEL[g.cceAllocation],
  };
}

function proposedResult(p: ProposedOutcome): ProposedResult {
  return {
    tacticDescription: p.description, nondeductible: toLine(p.cs), deductible: toLine(p.ss), addOns: toLine(p.addOns),
    total: Math.abs(p.total), saving: p.saving, releases: p.releases, converged: p.converged,
  };
}

function settingsChanged(c: Case): boolean {
  const d = defaultSettings() as unknown as Record<string, unknown>;
  const s = c.settings as unknown as Record<string, unknown>;
  return Object.keys(d).some((k) => JSON.stringify(d[k]) !== JSON.stringify(s[k]));
}

/** Full recalculation of a case (guideline, proposed, taxes, cash flow, per child). */
export function computeCase(c: Case, opts: EngineOptions = {}): CaseResult {
  const nc = normalizeCase(c);
  const provider = providerOf(opts);
  const now = opts.now ?? new Date();
  const ctx = new TaxContext(nc, provider);
  const g = computeGuideline(ctx, { releases: 0, releaseFrom: 1, now });
  const errors = [...nc.errors];
  if (c.settings.csMethod === 'GUIDELINE' && g.nets[0].formulaNet + g.nets[1].formulaNet < 0) {
    errors.push('ERROR!  The total unadjusted guideline net is negative. Please use UserCS to set child support.');
  }
  const pr = computeProposed(ctx, g, now);

  // Cash flow scenarios.
  const base = g.noSupportTaxes;
  const fn: [number, number] = [g.nets[0].formulaNet, g.nets[1].formulaNet];
  const scen = {
    noSupport: { spec: NO_SUPPORT, taxes: base, support: 0 } as ScenarioInputs,
    guideline: { spec: g.scenario, taxes: g.taxes, support: g.total } as ScenarioInputs,
    proposed: { spec: pr.scenario, taxes: pr.taxes, support: pr.total } as ScenarioInputs,
  };
  const combined = (s: ScenarioInputs) => ([0, 1] as const).reduce<number>((a, p) => a + fn[p] + (base[p].incomeTax - s.taxes[p].incomeTax) + receivedBy(s.support, p), 0);
  const combinedNo = combined(scen.noSupport), combinedG = combined(scen.guideline), combinedP = combined(scen.proposed);
  const nsG: [number, number] = [0, 1].map((p) => fn[p] + (base[p].incomeTax - g.taxes[p].incomeTax) + receivedBy(g.total, p as PartyIndex)) as [number, number];

  const parties = [0, 1].map((i) => {
    const p = i as PartyIndex;
    const np = nc.parties[p];
    const n = g.nets[p];
    const r: PartyResult = {
      label: nc.labels[p],
      effectiveFilingStatus: np.status,
      grossIncome: n.gross,
      otherTaxableIncome: n.otherTaxable,
      nonTaxableIncome: n.nonTaxable,
      netDisposable: n.netDisposable,
      adjustedNet: g.adjustedNets[p],
      formulaNet: n.formulaNet,
      totalDeductionsExcludingTaxes: n.deductions,
      hardshipTotal: n.hardshipTotal,
      addOnTotal: np.addOnItems,
      timeshareAllKids: np.timeshareAllKids * 100,
      newMateIncome: g.taxes[p].newMateIncome,
      noSupport: partyScenario(ctx, p, scen.noSupport, base, fn[p], nsG[p], combinedNo, null),
      guideline: partyScenario(ctx, p, scen.guideline, base, fn[p], nsG[p], combinedG, null),
      proposed: partyScenario(ctx, p, scen.proposed, base, fn[p], nsG[p], combinedP, combinedG),
    };
    return r;
  }) as [PartyResult, PartyResult];

  const s = c.settings;
  const taxesChanged = c.parties.some((p) => Object.values(p.taxSettings).some((v) => v === false));
  const creditsBlocked = c.parties.some((p) => Object.values(p.creditOverride).some((v) => v === false));
  const flags = {
    // "Settings changed" covers program settings AND the per-party tax/credit overrides, so a report
    // never claims program defaults while taxes or credits have been turned off.
    settingsChanged: settingsChanged(c) || taxesChanged || creditsBlocked,
    taxesChanged,
    creditsBlocked,
    federalSsIncluded: s.includeSsAfter2018InFederal,
  };
  const pct = (v: number, b: number) => (Math.abs(b) > 0.005 ? ((v - b) / Math.abs(b)) * 100 : 0);
  return {
    taxYear: c.taxYear,
    partyLabels: nc.labels,
    errors,
    warnings: nc.warnings,
    flags,
    parties,
    guideline: guidelineResult(nc, g),
    proposed: proposedResult(pr),
    cashFlow: {
      guideline: { combinedNetSpendable: combinedG, percentChange: pct(combinedG, combinedNo) },
      proposed: { combinedNetSpendable: combinedP, percentChange: pct(combinedP, combinedNo) },
    },
    perChild: perChildAllocation(nc, opts, g),
    paymentDescriptions: [
      paymentDescription(nc.labels, g.presumedCs, g.ss, g.addOns, 'guideline'),
      paymentDescription(nc.labels, pr.cs, pr.ss, pr.addOns, 'proposed'),
    ],
  };
}
